import { CSSProperties, useState } from 'react';

import { useChartColors } from './useChartColors';
import { chartNoOutlineStyle } from './chartStyles';

interface ActiveCellOptions {
  /** Suppress the hover ring (single-slice / 100%-slice cases). */
  suppressActive?: boolean;
}

interface ActiveCellProps {
  stroke: string;
  strokeWidth: number;
  style: CSSProperties;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

/**
 * Shared hover behavior for pie slices: ring stroke + slight scale on the
 * active slice. Spread the returned props onto each <Cell>:
 *
 *   const { getCellProps } = usePieActiveCell();
 *   <Cell fill={...} {...getCellProps(index, { suppressActive: ... })} />
 */
export const usePieActiveCell = () => {
  const { pieActiveStroke } = useChartColors();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const getCellProps = (
    index: number,
    options: ActiveCellOptions = {}
  ): ActiveCellProps => {
    const isHovered = activeIndex === index;
    const showRing = isHovered && !options.suppressActive;

    return {
      stroke: showRing ? pieActiveStroke : 'transparent',
      strokeWidth: showRing ? 2 : 0,
      style: {
        ...chartNoOutlineStyle,
        // The scale feedback applies even when the ring is suppressed
        // (single/100% slice), matching the original per-chart behavior.
        transform: isHovered ? 'scale(1.04)' : 'scale(1)',
        transformOrigin: '50% 50%',
        transition: 'all 150ms ease',
      },
      onMouseEnter: () => setActiveIndex(index),
      onMouseLeave: () => setActiveIndex(null),
    };
  };

  return { activeIndex, getCellProps };
};
